import { useMemo, useState } from "react";
import MainApi from "../../../../services/mainApi.service";
import { ITasyCalendarLogs } from "../../../../models/tasy-calendar-logs";
import {
  DatePickerUncontrolled,
  DateRangeSelect,
  Table,
} from "@4uhub/lib4uhub";
import { useTranslation } from "react-i18next";
import { GridColDef } from "@mui/x-data-grid";
import { format, isValid } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Box, Stack } from "@mui/material";
import FiltersActions from "../../../../components/FiltersActions/FiltersActions";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const FORU_BUS_SCHED_ROUTE =
  process.env.REACT_APP_4UBUS + "/api/v1/ForuBusSchedCtrl";

const foruBusSchedService = new MainApi<ITasyCalendarLogs>(
  FORU_BUS_SCHED_ROUTE,
);

const translationPath = "page.4uBus.logs.tasyCalendar.";

const TasyCalendarsLogsPage = () => {
  const [update, setUpdate] = useState(0);

  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [enableFields, setEnableFields] = useState<boolean>(true);

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const isValidDate = (date: Date | null): boolean => {
    if (!date) return false;
    return isValid(date);
  };

  const props = useMemo(() => {
    return {
      StartDate: startDate
        ? startDate?.toISOString().substring(0, 10)
        : undefined,

      EndDate: endDate ? endDate?.toISOString().substring(0, 10) : undefined,

      Update: update,
    };
  }, [startDate, endDate, update]);

  const columns: GridColDef<ITasyCalendarLogs>[] = [
    {
      field: "typeCode",
      headerName: t(translationPath + "type_code") || "Type Code",
      maxWidth: 250,
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Created",
      maxWidth: 150,
      renderCell: (params) => {
        if (!params.value) return "-";

        const initialDate = new Date(params.value);
        return format(initialDate, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "identifier",
      headerName: t(translationPath + "identifier") || "Identifier",
      maxWidth: 250,
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "calendarIdentifier",
      headerName:
        t(translationPath + "calendar_identifier") || "Calendar Identifier",
      maxWidth: 250,
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "calendarDate",
      headerName: t(translationPath + "calendar_date") || "Calendar Date",
      renderCell: (params) => {
        if (!params.value) return "-";

        const initialDate = new Date(params.value);
        return format(initialDate, "P", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        });
      },
    },
    {
      field: "status",
      headerName: t(translationPath + "status") || "Status",
      maxWidth: 100,
      renderCell: (params) => {
        return params.value ?? 0;
      },
    },
    {
      field: "individualIdentifier",
      headerName:
        t(translationPath + "individual_identifier") || "Individual Identifier",
      maxWidth: 200,
      renderCell: (params) => {
        return params.value ?? 0;
      },
    },
    {
      field: "lastModified",
      headerName: t(translationPath + "last_modified") || "Last Modified",
      maxWidth: 150,
      renderCell: (params) => {
        if (!params.value) return "-";

        const initialDate = new Date(params.value);
        return format(initialDate, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
  ];

  return (
    <Table<ITasyCalendarLogs>
      showDefaultMacroFilters={false}
      service={foruBusSchedService}
      columns={columns}
      title={t(translationPath + "title")}
      searchInput={false}
      serviceProps={props}
      showDelete={false}
      showDeleteButton={false}
      showButton={false}
      defaultActions={false}
      searchInputPlaceHolder={t(translationPath + "search") || ""}
      addButtonLabel={t(translationPath + "add_access_token") || ""}
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
      macroFilters={() => {
        return (
          <Stack flexDirection={"row"} gap={2}>
            <Box sx={{ flex: "0 0 200px" }}>
              <DateRangeSelect
                setEndDate={setEndDate}
                setStartDate={setStartDate}
                setEnableFields={setEnableFields}
              />
            </Box>

            <Box sx={{ flex: "0 0 170px" }}>
              <DatePickerUncontrolled
                value={startDate}
                onChange={setStartDate}
                label={`${t(translationPath + "start_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
            <Box sx={{ flex: "0 0 170px" }}>
              <DatePickerUncontrolled
                value={endDate}
                onChange={setEndDate}
                label={`${t(translationPath + "end_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
            <FiltersActions showCleanBtn={false} />
          </Stack>
        );
      }}
    />
  );
};

export default TasyCalendarsLogsPage;
